import React from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/Navbar';
import AdminSidebar from './components/AdminSidebar';
import GuestSidebar from './components/GuestSidebar';
import AdminHeader from './components/AdminHeader';
import GuestProfileBar from './components/GuestProfileBar';
import UserProfileModal from './components/UserProfileModal';
import './agency-admin.css';
import GettingStarted from './pages/GettingStarted';
import WelcomePage from './pages/WelcomePage';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import SuperAdminLogin from './pages/SuperAdminLogin';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminMarketplace from './pages/SuperAdminMarketplace';
import AdminDashboard from './pages/AdminDashboard';
import AgencyRegister from './pages/Agency/AgencyRegister';
import AgencyLogin from './pages/Agency/AgencyLogin';
import AgencyDashboard from './pages/Agency/AgencyDashboard';
import Budget from './pages/Budget';
import CreatePost from './pages/CreatePost';
import Ceremonies from './pages/Ceremonies';
import Settings from './pages/Settings';
import GuestJoin from './pages/GuestJoin';
import GuestList from './pages/GuestList';
import Tasks from './pages/Tasks';
import GuestDashboard from './pages/GuestDashboard';
import GuestTasks from './pages/GuestTasks';
import GuestSchedule from './pages/GuestSchedule';
import Gallery from './pages/Gallery';
import MehendiPreview from './pages/MehendiPreview';
import VendorManagement from './pages/VendorManagement';
import GuestVendors from './pages/GuestVendors';
import DebugConfig from './pages/DebugConfig';
import Register from './pages/Register';
import { InviteAuthProvider } from './pages/Invite/InviteAuthContext';
import InviteLayout from './pages/Invite/InviteLayout';
import InviteRegister from './pages/Invite/InviteRegister';
import InviteLogin from './pages/Invite/InviteLogin';
import InviteDashboard from './pages/Invite/InviteDashboard';
import InviteWizard from './pages/Invite/InviteWizard';
import InviteCampaignDetail from './pages/Invite/InviteCampaignDetail';
import InviteRsvpPage from './pages/Invite/InviteRsvpPage';
import InviteSummary from './pages/Invite/InviteSummary';
import InviteUpgradePage from './pages/Invite/InviteUpgradePage';
import InviteLandingPage from './pages/Invite/InviteLandingPage';
import InviteWhatsAppSettings from './pages/Invite/InviteWhatsAppSettings';

import FoodManagement from './pages/FoodManagement';
import GuestFood from './pages/GuestFood';
import Pricing from './pages/Pricing';
import PhotographerDashboard from './pages/PhotographerDashboard';
import AdminPhotographers from './pages/AdminPhotographers';
import PublicWeddingView from './pages/PublicWeddingView';
import PublicGallery from './pages/PublicGallery';
import LostAndFound from './pages/LostAndFound';
import MenuManagement from './pages/MenuManagement';
import GuestMenu from './pages/GuestMenu';
import WeddingPlanner from './pages/WeddingPlanner';
import ShadiWala from './pages/ShadiWala';
import Marketplace from './pages/Marketplace';
import VendorDetail from './pages/VendorDetail';
import MyCart from './pages/MyCart';
import BottomNav from './components/BottomNav';
import CulturalGuestPage from './pages/CulturalGuestPage';


// Wrapper to provide familySide to BottomNav from auth context
const BottomNavWrapper: React.FC = () => {
  return <BottomNav />;
};

const AppRoutes: React.FC = () => {
  return (
    <Switch>
      <Route path="/" exact component={LandingPage} />
      <Route path="/home" exact component={GettingStarted} />
      <Route path="/marketplace" exact component={Marketplace} />
      <Route path="/cart" exact component={MyCart} />
      <Route path="/vendor/:id" component={VendorDetail} />
      <Route path="/welcome" component={WelcomePage} />
      <Route path="/login" component={Login} />
      <Route path="/register" component={Register} />
      <Route path="/superadmin/login" component={SuperAdminLogin} />
      <Route path="/superadmin/dashboard" component={SuperAdminDashboard} />
      <Route path="/superadmin/marketplace" component={SuperAdminMarketplace} />
      <Route path="/agency" render={() => {
          // In local development, the main app doesn't serve the separate agencyend module.
          // We show a helper page to redirect to the Vite dev server.
          if (window.location.hostname === 'localhost' || window.location.hostname.includes('192.168')) {
              return (
                  <div style={{ 
                      height: '80vh', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      textAlign: 'center',
                      padding: '2rem',
                      background: 'var(--bg-color)'
                  }}>
                      <h2 style={{ color: 'var(--primary-color)', marginBottom: '1rem' }}>Agency Portal Redirect</h2>
                      <p style={{ maxWidth: '500px', marginBottom: '2rem', opacity: 0.8 }}>
                          You are in development mode. The new Agency module runs on its own Vite server (usually port 5173).
                      </p>
                      <a href="http://localhost:5173/agency" className="btn-luxury" style={{ textDecoration: 'none' }}>
                          Launch New Agency Portal (Port 5173)
                      </a>
                  </div>
              );
          }
          // In production, this should ideally be handled by Netlify, 
          // but if we are here, it means the main app is still controlling the route.
          window.location.href = '/agency/';
          return null;
      }} />
      <Route path="/agency/register" component={AgencyRegister} />
      <Route path="/agency/login" component={AgencyLogin} />
      <Route path="/admin/dashboard" component={AdminDashboard} />
      <Route path="/budget" component={Budget} />
      <Route path="/admin/create-post" component={CreatePost} />
      <Route path="/admin/ceremonies" component={Ceremonies} />
      <Route path="/admin/settings" component={Settings} />
      <Route path="/join/:token" component={GuestJoin} />
      <Route path="/admin/guests" component={GuestList} />
      <Route path="/admin/tasks" component={Tasks} />
      <Route path="/dashboard" component={GuestDashboard} />
      <Route path="/guest/tasks" component={GuestTasks} />
      <Route path="/guest/schedule" component={GuestSchedule} />
      <Route path="/admin/gallery" component={Gallery} />
      <Route path="/admin/vendors" component={VendorManagement} />
      <Route path="/guest/mehendi-ar" component={MehendiPreview} />
      <Route path="/guest/vendors" component={GuestVendors} />
      <Route path="/digital-album" component={Gallery} />
      <Route path="/guest/gallery" component={Gallery} />
      <Route path="/admin/food-management" component={FoodManagement} />
      <Route path="/guest/food" component={GuestFood} />
      <Route path="/admin/photographers" component={AdminPhotographers} />
      <Route path="/pricing" component={Pricing} />
      <Route path="/photographer/dashboard" component={PhotographerDashboard} />
      <Route path="/w/:accessKey" exact component={PublicWeddingView} />
      <Route path="/w/:accessKey/gallery" component={PublicGallery} />
      <Route path="/guest/lost-found" component={LostAndFound} />
      <Route path="/admin/menu" component={MenuManagement} />
      <Route path="/guest/menu" component={GuestMenu} />
      <Route path="/admin/wedding-planner" component={WeddingPlanner} />
      <Route path="/admin/shadi-wala" component={ShadiWala} />
      <Route path="/debug" component={DebugConfig} />
      <Route path="/be-a-guest" component={CulturalGuestPage} />
      
      {/* Standalone Invitation Module */}
      <Route path="/invite/rsvp/:token" component={InviteRsvpPage} />
      <Route path="/invite" render={() => (
        <Switch>
          <Route exact path="/invite" component={InviteLandingPage} />
          <Route exact path="/invite/register" component={InviteRegister} />
          <Route exact path="/invite/login" component={InviteLogin} />
          <Route render={() => (
            <InviteLayout>
              <Switch>
                <Route exact path="/invite/dashboard" component={InviteDashboard} />
                <Route exact path="/invite/upgrade" component={InviteUpgradePage} />
                <Route exact path="/invite/whatsapp/settings" component={InviteWhatsAppSettings} />
                <Route exact path="/invite/new" component={InviteWizard} />
                <Route exact path="/invite/campaign/:id" component={InviteCampaignDetail} />
                <Route exact path="/invite/summary/:id" component={InviteSummary} />
                <Redirect to="/invite/dashboard" />
              </Switch>
            </InviteLayout>
          )} />
        </Switch>
      )} />
    </Switch>
  );
};

const AppLayoutWrapper: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  const isAdminPath = location.pathname.startsWith('/admin') || location.pathname === '/budget';
  const isGuestPath = location.pathname === '/dashboard' || location.pathname.startsWith('/guest/');
  const isInvitePath = location.pathname.startsWith('/invite');

  const showAdminLayout = isAuthenticated && user?.role === 'admin' && isAdminPath;
  const showGuestLayout = isAuthenticated && user?.role === 'guest' && isGuestPath;
  const showWeddingLayout = showAdminLayout || showGuestLayout;

  React.useEffect(() => {
    if (showWeddingLayout) {
      document.documentElement.classList.add('agency-theme-active');
    } else {
      document.documentElement.classList.remove('agency-theme-active');
    }
    return () => {
      document.documentElement.classList.remove('agency-theme-active');
    };
  }, [showWeddingLayout]);

  return (
    <div className="app-container">
      <UserProfileModal />
      {showWeddingLayout ? (
        <>
          {showAdminLayout ? <AdminSidebar /> : <GuestSidebar />}
          <div className="admin-agency-layout-container">
            <AdminHeader />
            <main className="main-content">
              {showGuestLayout && <GuestProfileBar />}
              <AppRoutes />
            </main>
          </div>
        </>
      ) : (
        <>
          {!isInvitePath && <Navbar />}
          <main className={isInvitePath ? "main-content-invite" : "main-content"}>
            <AppRoutes />
          </main>
          {!isInvitePath && <BottomNavWrapper />}
        </>
      )}
    </div>
  );
};

const AppContent: React.FC = () => {
  return (
    <Router>
      <AppLayoutWrapper />
    </Router>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <InviteAuthProvider>
          <AppContent />
        </InviteAuthProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
